import { Inject, Injectable, PLATFORM_ID, makeStateKey } from '@angular/core'
import { isPlatformBrowser } from '@angular/common'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { TransferState } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { Localization } from '@aa-app-localization'

import { environment } from '@app-environments/environment'
import { CookieAuthStorageService } from '@app-services/auth-storage/cookie-auth-storage.service'
import {
    PRODUCT_RECOMMENDATIONS_QUERY,
    PRODUCT_SEARCH_QUERY,
    ProductSearchHit,
    ProductSearchInput,
    ProductSearchResult,
} from '@app-domains/meilisearch/types/product-search.types'

type GraphQlProductSearchResponse = {
    data?: {
        productSearch: ProductSearchResult
    }
    errors?: Array<{ message: string }>
}

type GraphQlProductRecommendationsResponse = {
    data?: {
        productRecommendations: ProductSearchHit[]
    }
    errors?: Array<{ message: string }>
}

@Injectable({ providedIn: 'root' })
export class ProductSearchGatewayService {
    private readonly graphqlUrl = `${environment.api.schema}://${environment.api.hostname}/graphql?op=ProductSearch`

    constructor(
        private readonly httpClient: HttpClient,
        private readonly transferState: TransferState,
        private readonly authStateStore: CookieAuthStorageService,
        @Inject(PLATFORM_ID) private readonly platformId: object,
    ) {
    }

    public async search(input: ProductSearchInput): Promise<ProductSearchResult> {
        const isBrowser = isPlatformBrowser(this.platformId)
        const cacheKey = makeStateKey<string>(`productSearch(${JSON.stringify(input)})`)

        if (! isBrowser && this.transferState.hasKey(cacheKey)) {
            return JSON.parse(this.transferState.get(cacheKey, '{}')) as ProductSearchResult
        }

        const token = this.authStateStore.getAuthState()?.accessToken
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        })

        const response = await firstValueFrom(this.httpClient.post<GraphQlProductSearchResponse>(
            this.graphqlUrl,
            {
                operationName: 'ProductSearch',
                query: PRODUCT_SEARCH_QUERY,
                variables: { input },
            },
            { headers },
        ))

        if (response.errors?.length) {
            throw new Error(response.errors.map((error) => error.message).join('; '))
        }

        const result = response.data?.productSearch

        if (! result) {
            throw new Error('productSearch returned no data')
        }

        if (! isBrowser) {
            this.transferState.set(cacheKey, JSON.stringify(result))
        }

        return result
    }

    public async recommendations(
        ean: string,
        locale: Localization.Locale,
        limit: number,
    ): Promise<ProductSearchHit[]> {
        const graphqlUrl = `${environment.api.schema}://${environment.api.hostname}/graphql?op=ProductRecommendations`
        const token = this.authStateStore.getAuthState()?.accessToken
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        })

        const response = await firstValueFrom(this.httpClient.post<GraphQlProductRecommendationsResponse>(
            graphqlUrl,
            {
                operationName: 'ProductRecommendations',
                query: PRODUCT_RECOMMENDATIONS_QUERY,
                variables: { ean, locale, limit },
            },
            { headers },
        ))

        if (response.errors?.length) {
            throw new Error(response.errors.map((error) => error.message).join('; '))
        }

        return response.data?.productRecommendations ?? []
    }
}
